import type { Project } from '@/redux/service/project'

export const NUMERIC_SORT_FIELDS = new Set([
  'total_hours',
  'remaining_hours',
  'tracked_duration_seconds',
  'billable_duration_seconds',
  'nonbillable_duration_seconds',
  'project_billable_amount',
  'billable_profit',
  'gross_profit',
  'net_profit',
  'total_employee_cost',
  'overhead_cost',
  'fully_loaded_cost',
  'task_count',
  'archived',
  'list_date_created',
  'start_date',
  'due_date',
  'cost',
  'total_budget',
  'hourly_cost',
  'bot_alerted',
])

function parseSortNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const normalized = String(value).replace(/[$,%\s]/g, '')
  if (normalized === '') return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function isEmptySortValue(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

function toSortNumber(value: unknown): number | null {
  return parseSortNumber(value)
}

function toSortString(value: unknown): string | null {
  if (isEmptySortValue(value)) return null
  return String(value)
}

function toSortTimestamp(value: unknown): number | null {
  if (isEmptySortValue(value)) return null
  const numeric = Number(value)
  if (Number.isFinite(numeric) && String(numeric) === String(value)) {
    return numeric
  }
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : null
}

export function compareTableValues(
  a: unknown,
  b: unknown,
  sortField: string
): number {
  const emptyA = isEmptySortValue(a)
  const emptyB = isEmptySortValue(b)
  if (emptyA && emptyB) return 0
  if (emptyA) return 1
  if (emptyB) return -1

  const shouldCompareAsNumber =
    NUMERIC_SORT_FIELDS.has(sortField) ||
    (parseSortNumber(a) !== null && parseSortNumber(b) !== null)

  if (shouldCompareAsNumber) {
    const numA = parseSortNumber(a)
    const numB = parseSortNumber(b)
    if (numA !== null && numB !== null) {
      return numA - numB
    }
    if (numA !== null) return -1
    if (numB !== null) return 1
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export function sortTableRows<T>(
  rows: T[],
  sortBy: string | undefined,
  order: 'ASC' | 'DESC' | undefined,
  getValue: (row: T, sortBy: string) => unknown
): T[] {
  if (!sortBy || !order || rows.length < 2) return rows

  return [...rows].sort((rowA, rowB) => {
    const comparison = compareTableValues(
      getValue(rowA, sortBy),
      getValue(rowB, sortBy),
      sortBy
    )
    return order === 'ASC' ? comparison : -comparison
  })
}

export function getProjectSortValue(
  project: Project,
  sortBy: string
): string | number | null {
  switch (sortBy) {
    case 'list_name':
      return toSortString(project.list_name)
    case 'client_name':
      return toSortString(project.client_name)
    case 'total_hours':
      return toSortNumber(project.total_hours)
    case 'tracked_duration_seconds':
      return toSortNumber(
        Number(project.billable_duration_seconds ?? 0) +
          Number(project.nonbillable_duration_seconds ?? 0)
      )
    case 'remaining_hours':
      return toSortNumber(project.remaining_hours)
    case 'billable_duration_seconds':
      return toSortNumber(project.billable_duration_seconds)
    case 'nonbillable_duration_seconds':
      return toSortNumber(project.nonbillable_duration_seconds)
    case 'project_billable_amount':
      return toSortNumber(
        project.billable_revenue ?? project.project_billable_amount
      )
    case 'billable_profit':
      return toSortNumber(project.billable_profit)
    case 'gross_profit':
      return toSortNumber(project.gross_profit)
    case 'net_profit':
      return toSortNumber(project.net_profit)
    case 'total_employee_cost':
      return toSortNumber(project.total_employee_cost)
    case 'overhead_cost':
      return toSortNumber(project.overhead_cost)
    case 'fully_loaded_cost':
      return toSortNumber(project.fully_loaded_cost)
    case 'folder_name':
      return toSortString(project.folder_name)
    case 'list_status':
    case 'status':
      return toSortString(project.status)
    case 'project_type':
      return toSortString(project.project_type)
    case 'assignee_name':
      return toSortString(project.assignee_name ?? project.assignee_username)
    case 'assignee_username':
      return toSortString(project.assignee_username)
    case 'account_manager':
      return toSortString(project.account_manager)
    case 'delivery_head':
      return toSortString(project.delivery_head)
    case 'qa_status':
      return toSortString(project.qa_status)
    case 'archived':
      return toSortNumber(project.archived)
    case 'space_name':
      return toSortString(project.space_name)
    case 'task_count':
      return toSortNumber(project.task_count)
    case 'content':
      return toSortString(project.content)
    case 'start_date':
      return toSortTimestamp(project.start_date)
    case 'due_date':
      return toSortTimestamp(project.due_date)
    case 'list_date_created':
      return toSortTimestamp(project.list_date_created)
    default:
      return null
  }
}
